<template>
  <div ref="root" class="scroll-gallery">
    <div class="section-label scroll-gallery__label mb-d">
      {{ content.headline }}
    </div>

    <div ref="container" class="scroll-gallery__container">
      <div
        v-for="(item, index) in content.items"
        :key="item.title"
        ref="block"
        class="scroll-gallery__item"
      >
        <h2
          ref="titles"
          :class="{ 'is-active': index === 0 }"
          class="scroll-gallery__title h2-custom"
        >
          {{ item.title }}
        </h2>
        <div ref="text" class="scroll-gallery__text" v-html="item.text"></div>
      </div>
    </div>

    <div ref="imagesContainer" class="scroll-gallery__images">
      <DraggableImages>
        <template v-for="item in content.items" :key="item.title">
          <div
            v-for="(picture, index) in item.pictures"
            :key="picture.src"
            ref="images"
            class="scroll-gallery__image"
            data-scroll-image
            :style="{
              transform: `rotate(${rand(index % 2 === 0).deg})`,
            }"
          >
            <Picture
              :content="{ picture }"
              :is-polaroid="true"
              data-draggable
              sizes="1280px"
            />
          </div>
        </template>
      </DraggableImages>
    </div>
  </div>
</template>

<script setup lang="ts">
/* eslint-disable no-mixed-operators */
import { gsap } from 'gsap'
import ScrollTrigger from 'gsap/ScrollTrigger'
import { onBeforeUnmount, onMounted, ref } from 'vue'
import mobileCheck from 'ismobilejs'

import DraggableImages from '@/components/global/DraggableImages.vue'
import Picture from '@/components/global/Picture.vue'
import { useUiStore } from '@/stores/ui'
import { useResize } from '@/utils/aware'
import { Picture as PictureType } from '@/types'

gsap.registerPlugin(ScrollTrigger)

interface ScrollGallery {
  headline: string
  items: {
    title: string
    text: string
    pictures: PictureType[]
  }[]
}

defineProps({
  content: {
    type: Object as () => ScrollGallery,
    required: true,
  },
})

const ui = useUiStore()

const isIos = mobileCheck().apple.device
const root = ref<HTMLElement>({} as HTMLElement)
const block = ref<HTMLElement[]>([])
const text = ref<HTMLElement[]>([])
const container = ref<HTMLElement>({} as HTMLElement)
const imagesContainer = ref<HTMLElement>({} as HTMLElement)
const images = ref<HTMLElement[]>([])

const rand = (negative: boolean) => {
  const scale = gsap.utils.random(50, 100, 5)
  const deg = negative
    ? gsap.utils.random(-10, -2, 1)
    : gsap.utils.random(10, 2, 1)

  return { deg: `${deg}deg`, scale }
}

const isMobile = ref(false)
const blockTimings: { startAt: number; finishAt: number }[] = []
const blockTls: gsap.core.Timeline[] = []

const getBlockTl = (block: HTMLElement, isLast: boolean) => {
  // const movement = isMobile.value ? { y: 30 } : { y: 100 }
  const tl = gsap.timeline({ paused: true })
  const text = block.querySelector('.scroll-gallery__text')

  tl.fromTo(
    text,
    { xPercent: 30 },
    {
      xPercent: isLast ? 0 : -30,
      ease: 'none',
    },
    '0'
  )

  return tl
}

const onUpdate = ({ progress }: { progress: number }) => {
  blockTimings.forEach((timing, index) => {
    if (progress > timing.startAt && progress < timing.finishAt) {
      const p = gsap.utils.mapRange(
        timing.startAt,
        timing.finishAt,
        0,
        1,
        progress
      )
      blockTls[index].progress(p)
    }
  })
}

// eslint-disable-next-line id-length
const updateIosScrollTriggerSettings = (config: ScrollTrigger.ConfigVars) => {
  if (isIos) {
    ScrollTrigger.config(config)
  }
}

const imagesAnimation = (totalDistance: number, wrapperOutside: number) => {
  const imagesContainerWidth = images.value.reduce(
    (acc, value) => acc + value.offsetWidth,
    0
  )

  const startOffset = 200
  const imagesContainerTl = gsap.timeline({
    paused: true,
    scrollTrigger: {
      start: `top+=${startOffset} center`,
      end: `+=${totalDistance - startOffset}`,
      scrub: 1,
      trigger: root.value,
    },
  })

  imagesContainerTl.fromTo(
    imagesContainer.value,
    { x: window.innerWidth - wrapperOutside },
    {
      x: -imagesContainerWidth + root.value.offsetWidth - wrapperOutside,
      willChange: 'transform',
      ease: 'none',
    }
  )

  const figures = imagesContainer.value.querySelectorAll('[data-scroll-image]')
  ;[...figures].forEach((image, index) => {
    imagesContainerTl.to(
      image,
      {
        rotate:
          index % 2
            ? `+=${gsap.utils.random(5, 20, 1)}`
            : `-=${gsap.utils.random(5, 20, 1)}`,
        ease: 'none',
      },
      '0'
    )
  })
}

const mainBlocksAnimation = (
  totalDistance: number,
  blocksDistance: number,
  wrapperOutside: number
) => {
  gsap.fromTo(
    container.value,
    { x: window.innerWidth - wrapperOutside },
    {
      x: -blocksDistance,
      ease: 'none',
      scrollTrigger: {
        start: 'top center',
        end: `+=${totalDistance}`,
        scrub: 1,
        trigger: root.value,
        onUpdate,
      },
    }
  )
}

let mainTrigger: ScrollTrigger
const initAnimation = () => {
  isMobile.value = !ui.isSmallLarger

  const wrapperOutside = (window.innerWidth - root.value.offsetWidth) / 2
  const blocksDistance = block.value.reduce(
    (prev, val, index) =>
      // Last block stays visible so we dont need it's width
      prev +
      (index === block.value.length - 1 ? -wrapperOutside : val.offsetWidth),
    wrapperOutside
  )
  const totalDistance =
    window.innerWidth -
    wrapperOutside +
    blocksDistance -
    block.value[0].offsetWidth * 0.3
  mainTrigger = ScrollTrigger.create({
    trigger: root.value,
    start: `top-=${isMobile.value ? 20 : 0} top`,
    end: `+=${totalDistance}`,
    toggleClass: 'is-pinned',
    pin: true,
  })

  mainBlocksAnimation(totalDistance, blocksDistance, wrapperOutside)
  imagesAnimation(totalDistance, wrapperOutside)

  let startAt = 0
  block.value.forEach((item, index) => {
    const isLast = index === block.value.length - 1
    const blockTravelDistance =
      // Last block stays visible so we need dont need it's width
      // prettier-ignore
      isLast
        ? window.innerWidth - wrapperOutside + item.offsetLeft
        // Uncomment to set the timing until the block disappears completely
        // : window.innerWidth - wrapperOutside + item.offsetLeft
        : window.innerWidth - wrapperOutside + item.offsetLeft + item.offsetWidth
    // prettier-ignore
    const distance = gsap.utils.mapRange(0, totalDistance, 0, 1, blockTravelDistance)
    const width = gsap.utils.mapRange(0, totalDistance, 0, 1, item.offsetWidth)

    blockTimings.push({ startAt, finishAt: distance })
    blockTls.push(getBlockTl(item, isLast))
    startAt += width
  })
}

onMounted(() => {
  updateIosScrollTriggerSettings({ syncInterval: 999999999 })
  initAnimation()

  // For slower devices
  setTimeout(() => {
    mainTrigger && mainTrigger.refresh()
  }, 3000)
})

onBeforeUnmount(() => {
  updateIosScrollTriggerSettings({ syncInterval: 200 })
})

useResize(() => {
  mainTrigger && mainTrigger.refresh()
})
</script>

<style lang="scss">
$bp: m;

.scroll-gallery {
  // prettier-ignore
  @include fluid(margin-top,(xxs: 60px, xxl: 150px));
}

.scroll-gallery__label {
  text-align: center;
  transition: opacity 0.15s;

  .is-pinned & {
    opacity: 0;
  }
}

.scroll-gallery__container {
  display: flex;
}

.scroll-gallery__item {
  flex-shrink: 0;
  min-width: 30rem;

  &:not(:last-child) {
    // Using padding to get real width in JS
    // prettier-ignore
    @include fluid(padding-right,(xxs: 60px, xxl: 120px));
  }
}

.scroll-gallery__title {
  margin-bottom: 4rem;
}

.scroll-gallery__text-container {
  flex-shrink: 0;
}

.scroll-gallery__text {
  // prettier-ignore
  @include fluid(margin-bottom,(xxs: 40px, xl: 80px));

  max-width: 32rem;

  @include mq(s) {
    max-width: 50rem;
  }
}

.scroll-gallery__images {
  // @extend %picture-perspective;
  position: relative;
  z-index: 2;
  display: flex;
  align-items: flex-start;
  margin-top: -6rem;
}

.scroll-gallery__image {
  @extend %picture-perspective;

  min-width: 30rem;
  pointer-events: none;

  figcaption {
    margin-top: 1rem;
    font-size: 1.2rem;
    line-height: 1.3;
  }

  &:nth-child(even) {
    margin-top: 4rem;
  }

  @include mq(m) {
    min-width: 40rem;
  }
}
</style>
